import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, User } from '../api/client';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  requires2FA: boolean;
  pending2FAUserId: number | null;
  sessionValidated: boolean;

  // Actions
  login: (
    email: string,
    password: string,
    turnstileToken?: string
  ) => Promise<{ requires2FA?: boolean }>;
  verify2FA: (code: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    password: string,
    turnstileToken?: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  validateSession: () => Promise<boolean>;
  setUser: (user: User | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      error: null,
      requires2FA: false,
      pending2FAUserId: null,
      sessionValidated: false,

      login: async (email, password, turnstileToken) => {
        set({ isLoading: true, error: null, requires2FA: false });
        try {
          const response = await api.login(email, password, turnstileToken);

          if (response.success) {
            if (response.requires_2fa) {
              set({
                isLoading: false,
                requires2FA: true,
                pending2FAUserId: response.user_id!,
              });
              return { requires2FA: true };
            }

            // Store tokens in localStorage for subsequent API calls
            if (response.token) {
              localStorage.setItem('access_token', response.token);
            }
            if (response.refresh_token) {
              localStorage.setItem('refresh_token', response.refresh_token);
            }

            set({
              user: response.user!,
              isLoading: false,
              sessionValidated: true,
            });
            return {};
          } else {
            throw new Error('Login failed');
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      verify2FA: async (code) => {
        const { pending2FAUserId } = get();
        if (!pending2FAUserId) {
          throw new Error('No pending 2FA verification');
        }

        set({ isLoading: true, error: null });
        try {
          const response = await api.verify2FA(pending2FAUserId, code);

          if (response.success) {
            // Store tokens in localStorage for subsequent API calls
            if (response.token) {
              localStorage.setItem('access_token', response.token);
            }
            if (response.refresh_token) {
              localStorage.setItem('refresh_token', response.refresh_token);
            }

            set({
              user: response.user,
              isLoading: false,
              requires2FA: false,
              pending2FAUserId: null,
              sessionValidated: true,
            });
          } else {
            throw new Error('2FA verification failed');
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : '2FA verification failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      register: async (name, email, password, turnstileToken) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.register(name, email, password, turnstileToken);

          if (response.success) {
            // Store tokens in localStorage for subsequent API calls
            if (response.token) {
              localStorage.setItem('access_token', response.token);
            }
            if (response.refresh_token) {
              localStorage.setItem('refresh_token', response.refresh_token);
            }

            set({
              user: response.user,
              isLoading: false,
              sessionValidated: true,
            });
          } else {
            throw new Error('Registration failed');
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.logout();
        } catch (error) {
          console.error('Logout request failed:', error);
        }

        // Clear tokens from localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');

        set({
          user: null,
          error: null,
          requires2FA: false,
          pending2FAUserId: null,
          sessionValidated: true,
        });
      },

      refreshUser: async () => {
        try {
          const response = await api.getMe();
          if (response.success) {
            set({ user: response.user });
          }
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
      },

      validateSession: async () => {
        const { user } = get();

        // Check if we have tokens in localStorage
        const hasTokens = !!localStorage.getItem('access_token');

        // If no user in state and no tokens, we're not logged in
        if (!user && !hasTokens) {
          set({ sessionValidated: true });
          return false;
        }

        try {
          const response = await api.getMe();
          if (response.success) {
            set({ user: response.user, sessionValidated: true });
            return true;
          }
          // Clear tokens if validation failed
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          set({ user: null, sessionValidated: true });
          return false;
        } catch (error) {
          console.error('Session validation failed:', error);
          // Clear tokens if validation failed
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          set({ user: null, sessionValidated: true });
          return false;
        }
      },

      setUser: (user) => {
        set({ user, sessionValidated: true });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      // Only persist non-sensitive user info
      partialize: (state) => ({
        user: state.user
          ? {
              id: state.user.id,
              name: state.user.name,
              email: state.user.email,
              discord_id: state.user.discord_id,
              avatar: state.user.avatar,
              two_factor_enabled: state.user.two_factor_enabled,
            }
          : null,
      }),
    }
  )
);

// Session expiration listener
if (typeof window !== 'undefined') {
  window.addEventListener('auth:session-expired', () => {
    // Clear tokens immediately
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    useAuthStore.getState().logout();
    window.location.href = '/login?session=expired';
  });
}
